import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type {
  ExpenseSubcategory,
  UpdateExpensePartTax,
} from '@pflegenavi/frontend/api-nursing-home';
import type { RecurringItemTaxCodeDto } from '@pflegenavi/shared/api';
import { MoneyInput, Typography } from '@pflegenavi/web-components';
import type { PartDraft } from './expensePartsDrafts';

const NO_SUBCATEGORY = '__none__';
const NO_TAX = '__none__';

export interface ExpensePartRowProps {
  part: PartDraft;
  subcategories: ExpenseSubcategory[];
  groupedSubcategories: Array<{
    category: string;
    items: ExpenseSubcategory[];
  }>;
  taxCodes: RecurringItemTaxCodeDto[];
  readOnly: boolean;
  canRemove: boolean;
  onAmountChange: (amount: number) => void;
  onSubcategoryChange: (id: string | null) => void;
  onTaxCodeChange: (tax: UpdateExpensePartTax | null) => void;
  onRemove: () => void;
}

export const ExpensePartRow: FC<ExpensePartRowProps> = ({
  part,
  subcategories,
  groupedSubcategories,
  taxCodes,
  readOnly,
  canRemove,
  onAmountChange,
  onSubcategoryChange,
  onTaxCodeChange,
  onRemove,
}) => {
  const { t } = useTranslation();

  const resolvedSubcategory =
    subcategories.find((s) => s.id === part.expense_subcategory_id) ?? null;
  const selectedSubcategoryId = resolvedSubcategory?.id ?? NO_SUBCATEGORY;
  const selectedIsArchived = resolvedSubcategory?.archived === true;

  // If the current selection is archived, surface it in the dropdown so the
  // user can see (and change) what's set. Otherwise it would render blank.
  const groupedForRow = useMemo(() => {
    if (!resolvedSubcategory || !selectedIsArchived) {
      return groupedSubcategories;
    }
    const archivedCat = resolvedSubcategory.category;
    const exists = groupedSubcategories.some((g) => g.category === archivedCat);
    if (exists) {
      return groupedSubcategories.map((g) =>
        g.category === archivedCat
          ? { ...g, items: [...g.items, resolvedSubcategory] }
          : g
      );
    }
    return [
      ...groupedSubcategories,
      { category: archivedCat, items: [resolvedSubcategory] },
    ];
  }, [groupedSubcategories, resolvedSubcategory, selectedIsArchived]);

  // Match by tax_abbreviation + tax_amount as a stable identity; the snapshot
  // doesn't carry the original tax-code id, so we re-locate it here.
  const selectedTaxCode = useMemo(() => {
    const snap = part.expense_part_tax;
    if (!snap) {
      return null;
    }
    return (
      taxCodes.find(
        (tc) =>
          tc.tax_abbreviation === snap.tax_abbreviation &&
          Number(tc.tax_amount) === Number(snap.tax_amount)
      ) ?? null
    );
  }, [part.expense_part_tax, taxCodes]);
  const selectedTaxId = selectedTaxCode?.id ?? NO_TAX;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <MoneyInput
        size="small"
        label={t('accounting.expenses.parts.field.amount')}
        value={part.amount}
        onChange={(value) => onAmountChange(value ?? 0)}
        disabled={readOnly}
        allowNegative
        sx={{ width: 140 }}
      />

      <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
        <InputLabel id={`part-subcategory-${part._internalId}-label`}>
          {t('accounting.expenses.parts.field.subcategory')}
        </InputLabel>
        <Select
          labelId={`part-subcategory-${part._internalId}-label`}
          label={t('accounting.expenses.parts.field.subcategory')}
          value={selectedSubcategoryId}
          disabled={readOnly}
          onChange={(e) => {
            const val = e.target.value;
            onSubcategoryChange(val === NO_SUBCATEGORY ? null : val);
          }}
        >
          <MenuItem value={NO_SUBCATEGORY}>
            <em>{t('accounting.expenses.parts.subcategory-none')}</em>
          </MenuItem>
          {groupedForRow.flatMap(({ category, items }) => [
            <ListSubheader key={`header-${category}`}>
              {t(`accounting.expenses.category.${category}`)}
            </ListSubheader>,
            ...items.map((sub) => (
              <MenuItem key={sub.id} value={sub.id}>
                {sub.name}
                {sub.archived
                  ? ` (${t('accounting.expenses.parts.subcategory-archived')})`
                  : ''}
              </MenuItem>
            )),
          ])}
        </Select>
        {selectedIsArchived && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
            {t('accounting.expenses.parts.subcategory-archived-warning')}
          </Typography>
        )}
      </FormControl>

      <FormControl size="small" sx={{ flex: 1, minWidth: 140 }}>
        <InputLabel id={`part-tax-${part._internalId}-label`}>
          {t('accounting.expenses.parts.field.tax-code')}
        </InputLabel>
        <Select
          labelId={`part-tax-${part._internalId}-label`}
          label={t('accounting.expenses.parts.field.tax-code')}
          value={selectedTaxId}
          disabled={readOnly}
          onChange={(e) => {
            const val = e.target.value;
            if (val === NO_TAX) {
              onTaxCodeChange(null);
              return;
            }
            const picked = taxCodes.find((tc) => tc.id === val);
            if (!picked) {
              onTaxCodeChange(null);
              return;
            }
            onTaxCodeChange(taxCodeToSnapshot(picked));
          }}
        >
          <MenuItem value={NO_TAX}>
            <em>{t('accounting.expenses.parts.tax-code-none')}</em>
          </MenuItem>
          {taxCodes.map((tc) => (
            <MenuItem key={tc.id} value={tc.id}>
              {formatTaxCodeLabel(tc)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        size="small"
        onClick={onRemove}
        disabled={readOnly || !canRemove}
        aria-label={t('accounting.expenses.parts.remove-button')}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

function formatTaxCodeLabel(tc: RecurringItemTaxCodeDto): string {
  const suffix = tc.paragraph ? `: ${tc.paragraph}` : '';
  const percentage = parseFloat(tc.tax_amount) * 100;
  return `${tc.tax_name}${suffix} (${percentage}%)`;
}

function taxCodeToSnapshot(tc: RecurringItemTaxCodeDto): UpdateExpensePartTax {
  return {
    country_id: tc.country.id,
    tax_amount: tc.tax_amount,
    tax_name: tc.tax_name,
    tax_abbreviation: tc.tax_abbreviation ?? null,
    explanation_short: tc.explanation_short ?? null,
    explanation_long: tc.explanation_long ?? null,
    display_explanation: tc.display_explanation ?? null,
    paragraph: tc.paragraph ?? null,
  };
}
